import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Input, Badge } from "../../components/ui";
import { Plus, Search, ArrowRight, Loader2, Filter, X, Shield, MapPin, ShoppingBag, Package, AlertCircle } from "lucide-react";
import { getAssetDetailRoute } from "../../config/routes";
import { cn } from "../../lib/utils";
import { getHeroClass } from "../../lib/design-system";
import { useProperty } from "../../contexts/PropertyContext";
import { getHomeAssets, lookupProductImage, updateAsset, toAbsoluteImageUrl, Asset, ASSET_CATEGORIES } from "../../services/asset.api";
import { AddAssetWizard } from "../../components/AddAssetWizard";
import { BrandSmartImportModal } from "../../components/modals/BrandSmartImportModal";
import { calculateHealthScore, getHealthLabel, getWarrantyStatus } from "../../utils/healthScore";

const categories = ["All", ...ASSET_CATEGORIES];

// Map from a normalized product type to the broad Asset category used in the DB

// â”€â”€â”€ Health score helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function getHealthTextClass(score: number): string {
  if (score >= 8) return 'text-green-600';
  if (score >= 6) return 'text-yellow-600';
  if (score >= 4) return 'text-orange-600';
  return 'text-red-600';
}

function getHealthBarClass(score: number): string {
  if (score >= 8) return 'bg-green-500';
  if (score >= 6) return 'bg-yellow-500';
  if (score >= 4) return 'bg-orange-500';
  return 'bg-red-500';
}

function computeEffectiveVariant(asset: Asset): string {
  const w = getWarrantyStatus(asset.warrantyExpiresAt);
  if (w.variant !== 'unknown') return w.variant;
  const bsActive = asset.manufacturerWarrantyStatus === 'active' || asset.extendedWarrantyStatus === 'active';
  if (bsActive) return 'active';
  const bsExpired = asset.manufacturerWarrantyStatus === 'expired' || asset.extendedWarrantyStatus === 'expired';
  if (bsExpired) return 'expired';
  return 'unknown';
}

// â”€â”€â”€ Asset Card component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface AssetCardProps {
  asset: Asset;
  index: number;
  serpUrl: string | undefined;
  onImageError: (asset: Asset) => void;
  onNavigate: (id: string) => void;
  serpFetchingRef: React.RefObject<Set<string>>;
}

function AssetCard({ asset, index, serpUrl, onImageError, onNavigate, serpFetchingRef }: Readonly<AssetCardProps>) {
  const { t } = useTranslation('assets');
  const healthScore = calculateHealthScore(asset);
  const effectiveVariant = computeEffectiveVariant(asset);
  const src = toAbsoluteImageUrl(serpUrl || asset.productImageUrl || asset.imageUrl || '');
  const [imgFailed, setImgFailed] = useState(false);

  const hasUserImage = !!(asset.productImageUrl || asset.imageUrl);

  React.useEffect(() => {
    // New src (e.g. a SERP image just resolved) → give it a fresh chance to render.
    setImgFailed(false);
    if (!src && !hasUserImage && !serpFetchingRef.current?.has(asset.id)) {
      onImageError(asset);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id, src]);

  return (
    <button
      type="button"
      onClick={() => onNavigate(asset.id)}
      className={cn(
        "group cursor-pointer rounded-2xl border bg-card overflow-hidden text-left w-full",
        "transition-all duration-500 hover:shadow-xl hover:-translate-y-1",
        "opacity-0 animate-fade-in-up"
      )}
      style={{ animationDelay: `${index * 0.07}s`, animationFillMode: "forwards" }}
    >
      {/* Image */}
      <div className="relative overflow-hidden bg-muted/30" style={{ height: '200px' }}>
        {src && !imgFailed ? (
          <img
            key={src}
            src={src}
            onError={() => {
              setImgFailed(true);
              // The stored image failed to load (e.g. a dead BrandsMart manufacturer
              // CDN URL). Resolve a real one via SERP unless we already tried one —
              // the backend persists the result so the detail pages heal too.
              if (!serpUrl) {
                onImageError(asset);
              }
            }}
            alt={asset.name}
            referrerPolicy="no-referrer"
            className="h-full w-full object-contain bg-gray-50 transition-transform duration-700 group-hover:scale-105 p-2"
          />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center gap-2 bg-muted/20">
            <div className="h-14 w-14 rounded-2xl bg-muted flex items-center justify-center">
              <Package className="h-7 w-7 text-muted-foreground opacity-50" />
            </div>
            {asset.brand && <p className="text-xs font-medium text-muted-foreground">{asset.brand}</p>}
          </div>
        )}
        {/* Health Score Badge */}
        <div className="absolute top-4 right-4 h-11 w-11 rounded-full flex flex-col items-center justify-center shadow-lg bg-black/45 border border-white/20 backdrop-blur-md">
          <span className="text-sm font-bold leading-none text-white">{healthScore}</span>
          <span className="text-[7px] font-medium uppercase mt-0.5 text-white/80">{t('healthBadge')}</span>
        </div>
        {/* Warranty badge */}
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium shadow-sm backdrop-blur-md bg-black/45 border border-white/20 text-white">
            <Shield className="h-3 w-3" />
            {effectiveVariant === 'active' && t('warranty.active')}
            {effectiveVariant === 'expiring' && t('warranty.expiring')}
            {effectiveVariant === 'expired' && t('warranty.expired')}
            {effectiveVariant === 'unknown' && t('warranty.noWarranty')}
          </span>
        </div>
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        {/* Hover CTA */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
          <span className="text-white text-sm font-medium">{t('viewDetails')}</span>
          <div className="h-8 w-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <ArrowRight className="h-4 w-4 text-white" />
          </div>
        </div>
      </div>
      {/* Info */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-base leading-tight mb-1">{asset.name}</h3>
            <p className="text-sm text-muted-foreground">
              {asset.brand || t('unknown')} {asset.model ? `\u00b7 ${asset.model}` : ''}
            </p>
          </div>
        </div>
        {/* Health Score Bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">{t('healthScore')}</span>
            <span className={cn("text-xs font-semibold", getHealthTextClass(healthScore))}>
              {healthScore}/10 · {getHealthLabel(healthScore)}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div
              className={cn("h-1.5 rounded-full transition-all", getHealthBarClass(healthScore))}
              style={{ width: `${healthScore * 10}%` }}
            />
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t">
          <Badge variant="secondary" className="rounded-full text-xs font-medium">
            {asset.category}
          </Badge>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {asset.location || t('unassigned')}
          </span>
        </div>
      </div>
    </button>
  );
}

// â”€â”€â”€ Empty State component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface EmptyStateProps {
  isFiltering: boolean;
  currentProperty: { id: string; name: string } | null;
  onAddAsset: () => void;
  onNavigateProfile: () => void;
}

function AssetsEmptyState({ isFiltering, currentProperty, onAddAsset, onNavigateProfile }: Readonly<EmptyStateProps>) {
  const { t } = useTranslation('assets');
  let title = t('empty.noAssetsTitle');
  let description = t('empty.noAssetsDesc');
  if (isFiltering) {
    title = t('empty.noResultsTitle');
    description = t('empty.noResultsDesc');
  } else if (!currentProperty) {
    title = t('empty.noHomeTitle');
    description = t('empty.noHomeDesc');
  }

  let ctaButton: React.ReactNode = null;
  if (!isFiltering) {
    if (currentProperty) {
      ctaButton = (
        <Button onClick={onAddAsset} size="lg" className="mt-6 gap-2">
          <Plus className="h-4 w-4" />
          {t('empty.addFirstAsset')}
        </Button>
      );
    } else {
      ctaButton = (
        <Button onClick={onNavigateProfile} size="lg" className="mt-6 gap-2">
          <Plus className="h-4 w-4" />
          {t('empty.addFirstHome')}
        </Button>
      );
    }
  }

  return (
    <div className="text-center py-24 animate-fade-in">
      <div className="h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-6">
        {isFiltering ? (
          <Search className="h-7 w-7 text-muted-foreground" />
        ) : (
          <Package className="h-7 w-7 text-muted-foreground opacity-60" />
        )}
      </div>
      <h3 className="text-xl font-normal">{title}</h3>
      <p className="text-muted-foreground mt-2 max-w-sm mx-auto">{description}</p>
      {ctaButton}
    </div>
  );
}

// â”€â”€â”€ Main Page component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function Assets() {
  const navigate = useNavigate();
  const { t } = useTranslation('assets');
  const { currentProperty } = useProperty();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBrandSmartModal, setShowBrandSmartModal] = useState(false);
  const [serpImageMap, setSerpImageMap] = useState<Map<string, string>>(new Map());
  const serpFetchingRef = React.useRef<Set<string>>(new Set());
  const handleImageError = React.useCallback(async (asset: Asset) => {
    if (serpFetchingRef.current.has(asset.id)) return;
    if (!asset.brand && !asset.model) return;
    serpFetchingRef.current.add(asset.id);
    try {
      const result = await lookupProductImage(
        asset.brand || '',
        asset.model || '',
        asset.productCategory || asset.subCategory || asset.category || '',
        {
          assetId: asset.id,
          forceRefresh: false,
          productTitle: asset.productTitle || undefined,
          assetType: asset.subCategory || asset.productCategory || undefined,
          manufacturer: asset.manufacturer || undefined,
          serial: asset.serialNumber || undefined,
        },
      );
      if (result.imageUrl) {
        setSerpImageMap(prev => new Map(prev).set(asset.id, result.imageUrl));
        // DB persistence is handled by the backend persistAssetImage() inside resolve-product-image.
        // Calling updateAsset() here with a stale asset closure would overwrite user-uploaded images.
        setAssets(prev => prev.map(a => a.id === asset.id ? { ...a, productImageUrl: result.imageUrl } : a));
      }
    } catch { /* non-fatal */ } finally {
      serpFetchingRef.current.delete(asset.id);
    }
  }, []);

  useEffect(() => {
    console.log('[Assets] currentProperty changed:', currentProperty);
  }, [currentProperty]);

  useEffect(() => {
    if (currentProperty?.id) {
      loadAssets();
    } else {
      setLoading(false);
      setAssets([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProperty?.id]);

  const loadAssets = async () => {
    if (!currentProperty?.id) {
      setLoading(false);
      setAssets([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await getHomeAssets(currentProperty.id);
      setAssets(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch =
      asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.category?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === "All" || asset.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const isFiltering = searchQuery.length > 0 || activeCategory !== "All";

  let sectionContent: React.ReactNode;
  if (loading) {
    sectionContent = (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  } else if (error) {
    sectionContent = (
      <div className="text-center py-24">
        <div className="h-16 w-16 rounded-2xl bg-red-100 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="h-7 w-7 text-red-500" />
        </div>
        <h3 className="text-xl font-normal">{t('error.loadFailed')}</h3>
        <p className="text-muted-foreground mt-2">{error}</p>
        <Button onClick={loadAssets} className="mt-4">{t('error.tryAgain')}</Button>
      </div>
    );
  } else if (filteredAssets.length === 0) {
    sectionContent = (
      <AssetsEmptyState
        isFiltering={isFiltering}
        currentProperty={currentProperty}
        onAddAsset={() => setShowAddModal(true)}
        onNavigateProfile={() => navigate('/profile', { state: { openAddModal: true } })}
      />
    );
  } else {
    sectionContent = (
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filteredAssets.map((asset, index) => (
          <AssetCard
            key={asset.id}
            asset={asset}
            index={index}
            serpUrl={serpImageMap.get(asset.id)}
            onImageError={handleImageError}
            onNavigate={(id) => navigate(getAssetDetailRoute(id))}
            serpFetchingRef={serpFetchingRef}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
    {/* Header */}
      <section className="border-b bg-background">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-5">
          {/* Title */}
          <div className="mb-4">
            <h1 className={getHeroClass('main')}>{t('title')}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('subtitle')}
            </p>
          </div>

          {/* Single action bar: count · search · filter | import · add */}
          <div className="flex items-center gap-2">
            {/* Asset count */}
            <span className="shrink-0 inline-flex items-center h-9 px-3 rounded-md border bg-muted/50 text-sm font-medium text-muted-foreground">
              {t('count', { count: filteredAssets.length })}
            </span>

            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder={t('search')}
                className="pl-9 h-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filter */}
            <div className="relative shrink-0">
              <Button
                variant={activeCategory === "All" ? "outline" : "default"}
                size="sm"
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="gap-1.5 h-9"
              >
                <Filter className="h-4 w-4" />
                {activeCategory === "All" ? t('filter') : activeCategory}
                {activeCategory !== "All" && (
                  <X
                    className="h-3 w-3 ml-0.5"
                    onClick={(e) => { e.stopPropagation(); setActiveCategory("All"); }}
                  />
                )}
              </Button>

              {showFilterDropdown && (
                <>
                  <div
                    role="presentation"
                    tabIndex={-1}
                    className="fixed inset-0 z-10"
                    onClick={() => setShowFilterDropdown(false)}
                    onKeyDown={(e) => e.key === 'Escape' && setShowFilterDropdown(false)}
                  />
                  <div className="absolute right-0 top-11 z-20 w-56 rounded-lg border bg-popover shadow-lg">
                    <div className="p-2">
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground mb-1">
                        {t('filterByCategory')}
                      </div>
                      {categories.map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => { setActiveCategory(cat); setShowFilterDropdown(false); }}
                          className={cn(
                            "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                            activeCategory === cat
                              ? "bg-primary text-primary-foreground font-medium"
                              : "hover:bg-muted"
                          )}
                        >
                          {cat}
                          {cat !== "All" && (
                            <span className="ml-2 text-xs opacity-70">
                              ({assets.filter(a => a.category === cat).length})
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-border shrink-0" />

            {/* Import from BrandsMart */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/brandsmart/import')}
              className="shrink-0 gap-1.5 h-9 border-blue-200 text-blue-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 transition-colors"
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="hidden sm:inline">{t('import')}</span>
            </Button>

            {/* Add Asset */}
            <Button
              size="sm"
              onClick={() => setShowAddModal(true)}
              className="shrink-0 gap-1.5 h-9"
            >
              <Plus className="h-4 w-4" />
              {t('addAsset')}
            </Button>
          </div>
        </div>
      </section>

      {/* Assets Grid */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
        {sectionContent}
      </section>

      {showAddModal && (
        <AddAssetWizard
          onClose={() => setShowAddModal(false)}
          onSuccess={loadAssets}
        />
      )}

      {showBrandSmartModal && (
        <BrandSmartImportModal
          onClose={() => setShowBrandSmartModal(false)}
          onSuccess={loadAssets}
        />
      )}
    </div>
  );
}